import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useLocale } from '@/context/LocaleContext';
import { useSendMessage, refreshMessagesUnreadCount, scheduleConversationsSync } from '@/hooks/useMessages';
import { resolveMediaUrl } from '@/lib/assetUrl';

export interface DropLogChatItem {
  id64: number;
  charId: number;
  charName16: string;
  itemName: string;
  itemIcon: string;
  refObjId: number;
}

interface DropItemChatModalProps {
  item: DropLogChatItem | null;
  currentCharId: number;
  onClose: () => void;
}

type SendPhase = 'idle' | 'sending' | 'sent' | 'error';

function ChatIcon({ size = 20 }: { size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

export function DropItemChatModal({ item, currentCharId, onClose }: DropItemChatModalProps) {
  const { t } = useLocale();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const sendMutation = useSendMessage();
  const [phase, setPhase] = useState<SendPhase>('idle');
  const [errorText, setErrorText] = useState('');

  useEffect(() => {
    if (!item) {
      setPhase('idle');
      setErrorText('');
    }
  }, [item]);

  const handleRequestItem = useCallback(async () => {
    if (!item || phase === 'sending') return;

    if (item.charId === currentCharId) {
      setPhase('error');
      setErrorText(t('log_chat_self_error'));
      return;
    }

    setPhase('sending');
    setErrorText('');

    const message = t('log_modal_message_template', { item: item.itemName });

    try {
      await sendMutation.mutateAsync({
        receiver: item.charId,
        message,
        id64: item.id64,
      });

      scheduleConversationsSync(queryClient, currentCharId, 300);
      void refreshMessagesUnreadCount(queryClient, currentCharId);

      setPhase('sent');

      window.setTimeout(() => {
        onClose();
        navigate('/messages', {
          state: {
            openChat: {
              charId: item.charId,
              charName: item.charName16,
              refObjId: item.refObjId,
            },
          },
        });
      }, 900);
    } catch {
      setPhase('error');
      setErrorText(t('log_modal_connection_error'));
    }
  }, [item, phase, currentCharId, t, sendMutation, queryClient, onClose, navigate]);

  if (!item) return null;

  const iconSrc = resolveMediaUrl(`/${item.itemIcon}`);

  return (
    <div
      className="drop-modal-overlay"
      role="presentation"
    >
      <div className="drop-modal-content drop-item-chat-modal" role="dialog" aria-modal="true" aria-labelledby="drop-chat-title">
        <div className="drop-modal-header">
          <h3 id="drop-chat-title">{t('log_chat_button_title')}</h3>
        <button
          type="button"
          className="drop-modal-close drop-item-chat-modal-close"
          onClick={onClose}
          disabled={phase === 'sending'}
          aria-label={t('messages_cancel')}
        >
          ×
        </button>
        </div>

        <div className="drop-modal-body drop-item-chat-modal-body">
          <div className="drop-item-chat-modal-icon-wrap">
            <img src={iconSrc} alt="" className="drop-item-chat-modal-icon" loading="lazy" />
          </div>
          <h3 className="drop-item-chat-modal-title">{item.charName16}</h3>
          <p className="drop-item-chat-modal-item">{item.itemName}</p>

          {phase === 'error' && errorText ? (
            <p role="alert" className="drop-item-chat-modal-error">{errorText}</p>
          ) : null}

        </div>
        <div className="drop-item-chat-modal-footer">
          <button type="button" className="drop-modal-btn" disabled={phase === 'sending' || phase === 'sent'} onClick={onClose}>{t('messages_cancel')}</button>
          <button
            type="button"
            className="drop-modal-btn drop-modal-btn-primary"
            disabled={phase === 'sending' || phase === 'sent'}
            onClick={() => void handleRequestItem()}
          >
            {phase === 'sent' ? (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#2ecc71"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                <span className="drop-item-chat-modal-sent-label">{t('log_modal_sent')}</span>
              </>
            ) : phase === 'sending' ? (
              <>
                <ChatIcon />
                <span>{t('log_modal_sending')}</span>
              </>
            ) : (
              <>
                <ChatIcon />
                <span>{t('log_modal_i_need_it')}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export function DropLogChatButton({
  onClick,
  title,
}: {
  onClick: () => void;
  title: string;
}) {
  return (
    <button
      type="button"
      className="drop-log-chat-btn tooltipped"
      data-tooltip={title}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      aria-label={title}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
    </button>
  );
}
